import getProjectFromJson from '@/requests/project/getProjectFromJson';
import { setAppStore } from '@/store';
import isAuthorized from '@/utils/user/isAuthorized';
import openRegistrationModal from '@/utils/user/openRegistrationModal';
import { InputType, PreviewMediaType, Screen } from '@/types';
import type { IInput, IProject, Preset } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type ProjectJsonDeeplink = Extract<
  Deeplink,
  { kind: DeeplinkKind.ProjectJson }
>;

const createPresetFromProject = (project: IProject): Preset => {
  const inputs = project.inputs ?? [];

  const textItems = inputs.filter((i: IInput) => i.type === InputType.TEXT);
  const uploadItems = inputs.filter(
    (i: IInput) => i.type === InputType.IMAGE || i.type === InputType.VIDEO
  );

  const previewMedia = project.layout?.sidebar?.header?.previewMedia;
  let video: Preset['video'] = [];
  let image: Preset['image'] = [];
  if (previewMedia?.src) {
    const media = {
      url: previewMedia.src,
      width: previewMedia.width || 0,
      height: previewMedia.height || 0,
    };
    if (previewMedia.type === PreviewMediaType.VIDEO) {
      video = [{ media: { ...media, poster: previewMedia.poster } }];
    } else {
      image = [{ media }];
    }
  }

  return {
    flowId: project.webhookId,
    type: project.resultType || PreviewMediaType.IMAGE,
    title: previewMedia?.title || '',
    description: previewMedia?.description || '',
    textItems,
    uploadItems,
    video,
    image,
  };
};

/**
 * presetJsonUrl deeplink: fetch the remote project, mount the pseudo-preset,
 * and if the user isn't authorized yet, open the registration modal.
 *
 * The router strips the URL after this resolves, so a reload (whether the
 * user logs in, dismisses, or completes generation) cannot re-arm the
 * deeplink. useAutoGeneration handles the actual generation trigger
 * reactively once auth + credits are ready, reading isDeeplinkSession from
 * the immutable snapshot.
 */
const projectJsonHandler = async ({
  jsonUrl,
}: ProjectJsonDeeplink): Promise<void> => {
  const project = await getProjectFromJson(jsonUrl);
  if (!project) return;

  const pseudoPreset = createPresetFromProject(project);
  setAppStore({
    isRemoteProject: true,
    selectedPreset: pseudoPreset,
    inputs: project.inputs ?? [],
    toolId: project.drive?.toolId,
    folderName: project.drive?.folderName,
    attributes: project.drive?.attributes,
    screen: Screen.Create,
    hasAutoSelectedPreset: true,
  });

  if (!isAuthorized()) {
    openRegistrationModal();
  }
};

export default projectJsonHandler;
